import _ from 'lodash';
import { OrderedMap } from 'immutable';
import { filter } from 'fuzzaldrin';

import { getApolloClientSingleton } from 'lib/apollo/client/browser';

import Chat from 'entities/Chat';
import Chats from 'collections/Chats';
import PotentialChatMember from 'entities/PotentialChatMember';
import { TARGET_TYPES, Message } from 'entities/Message';
import { search, groupChatsQuery } from 'clients/api/roleBasedMessagingSearch';
import Collection from 'lib/Collection';
import RecipientCountQuery from '../queries/RecipientCount.gql';
import type {
  QueryCountRecipients,
  QueryCountRecipientsVariables,
} from '../queries/__generated__/QueryCountRecipients';
import { Target } from 'entities/Message';
import type { ChatPredefinedMessageTranslationsInput } from 'globalTypes.hq';
import type { ChatMessageTemplatesFile } from 'messageTemplates/gql';
import {
  MessageFilterType,
  StudentIdListRecipient,
  Recipient,
  GroupRecipient,
  GroupChatRecipient,
  RecipientCountsMap,
  ComposerGroup,
  ComposerOrganization,
  IndividualRecipient,
  OrganizationRecipient,
} from 'types/typescript/messagingTypes';
import { SelectedSocialMediaAccounts } from 'chat/components/ShareOnSocialMedia/ShareOnSocialMediaHoverCard';

const fuzzaldrinOptions = {
  key: 'searchString',
};

export const RECIPIENT_TYPES = {
  STUDENT_ID_LIST_RECIPIENT: 'student_id_list_recipient',
  ORGANIZATION_RECIPIENT: 'organization_recipient',
  GROUP_RECIPIENT: 'group_recipient',
  GROUP_CHAT_RECIPIENT: 'group_chat_recipient',
  INDIVIDUAL_RECIPIENT: 'individual_recipient',
} as const;

export type Envelope = {
  // for state transfer from modal to composer page
  draftAnnouncement?: Message;
  initialTargets?: Target[];
  messageType: 'message' | 'urgent';
  onMessageSent?: (options: { scheduledTime?: Date; targets: Target[] }) => void;
  initialSocialMediaAccounts?: SelectedSocialMediaAccounts;
  initialPVMFileUuid?: string;
  isCreatingMessageTemplate?: boolean;
  isEditingMessageTemplate?: boolean;
  isUsingMessageTemplate?: boolean;
  onSaveMessageTemplate?: (composerData: {
    body: string;
    files: ChatMessageTemplatesFile[];
    originalLanguageCode: string;
    targetLanguageCodes: string[];
    predefinedTranslations: ChatPredefinedMessageTranslationsInput[];
    personalizedVoiceMessageFile?: string | null;
  }) => Promise<{ success: boolean }>;
};

export type State = {
  recipients: Recipient[]; // flat list of all recipients
  results: Array<Recipient[]>; // recipients grouped by class
  resultSelectionIndex: number; // which of a class' results is selected
  selection: Recipient[]; // flat list of chosen recipients
  targets: Target[];
  query: string;
  highlightIndex: number;
  message: string;
  loadingIndividualRecipients?: boolean;
  loadingGroupChats?: boolean;
  districtIsSelected?: boolean;
  totalRecipientCount?: number; // only a reflection of the recipientCountsMap
  recipientCountsMap: RecipientCountsMap;
  envelope: Envelope;
  didSearchRecipients: boolean;
};

const initialState: State = {
  recipients: [],
  results: [],
  resultSelectionIndex: 0,
  selection: [],
  targets: [],
  query: '',
  highlightIndex: -1,
  message: '',
  totalRecipientCount: undefined,
  recipientCountsMap: OrderedMap(),
  envelope: { messageType: 'message' },
  didSearchRecipients: false,
};

export const createTargets = (recipients: Recipient[]): Target[] => {
  return recipients.map((r: Recipient) => {
    switch (r.type) {
      case RECIPIENT_TYPES.ORGANIZATION_RECIPIENT:
        return {
          type: TARGET_TYPES.ORGANIZATION_TARGET,
          org_id: r.organization.id,
          uuid: r.organization.uuid,
          selectedFilters: r.selectedFilters,
          filters: r.filters,
        };
      case RECIPIENT_TYPES.GROUP_RECIPIENT:
        return {
          type: TARGET_TYPES.GROUP_TARGET,
          group_id: r.group.id,
          uuid: r.group.uuid,
          selectedFilters: r.selectedFilters,
          filters: r.filters,
          role: 'everyone',
        };
      case RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT:
        return {
          type: TARGET_TYPES.GROUP_CHAT_TARGET,
          chat_id: r.chat.id,
          uuid: r.chat.id,
        };
      case RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT:
        return {
          type: TARGET_TYPES.INDIVIDUAL_TARGET,
          member_id: r.member.id,
          uuid: r.member.getUuid(),
        };
      case RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT:
        return {
          type: TARGET_TYPES.STUDENT_ID_LIST_TARGET,
          uuid: r.id,
          org_uuid: r.orgUuid,
        };
      default:
        // very defensive, should never expect this to get called, ever.
        throw new Error(`Bad type in recipient for createTargets - ${(r as any).type}`);
    }
  });
};

const filterIsGroupRecipient = (recipient: Recipient): recipient is GroupRecipient => {
  return recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT;
};

const generateResults = (recipients: Recipient[]) => {
  const org = recipients.filter((r) => r.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT);
  const group: GroupRecipient[] = recipients.filter(filterIsGroupRecipient);
  const groupChat = recipients.filter((r) => r.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT);
  // We want to surface newly created group chats.
  const orderedGroupChats = _.orderBy(groupChat, ['chat.attributes.sort_key'], ['desc']);
  const individual = recipients.filter((r) => r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT);
  const orgResults = _.values(_.groupBy([...org], (r) => r.id));
  const groupResults = _.values(_.groupBy([...group], (r) => r.id));
  const groupChatResults = _.values(_.groupBy([...orderedGroupChats], (r) => r.id));
  const individualResults = _.values(_.groupBy([...individual], (r) => r.id));
  // sort by the underlying backend data sort_key.
  // NOTE that this is not present in the type information partially because it is hidden under backbone
  const individualResultsSorted = _.orderBy(
    individualResults,
    '0.member.attributes.sort_key',
    'asc'
  );

  return [...orgResults, ...groupResults, ...groupChatResults, ...individualResultsSorted];
};

const calcResults = (recipients: Recipient[], query: string): Recipient[] => {
  if (query) {
    return filter(recipients, query, fuzzaldrinOptions);
  } else {
    return recipients;
  }
};

const createIndividualRecipient = (member: PotentialChatMember): IndividualRecipient => {
  return {
    type: 'individual_recipient',
    member,
    id: member.id.toString(),
    uuid: member.getUuid(),
    searchString: `${member.getName()} ${member.getFirstName()} ${member.getLastName()} ${member.getNote()} ${member.getGroupNames()}`,
  };
};

const createOrgRecipient = (organization: ComposerOrganization): OrganizationRecipient => {
  const { messageTargetFilters } = organization;
  const filters = messageTargetFilters
    ? _.flatMap(messageTargetFilters, (messageTargetFilter) => {
        return messageTargetFilter.values.map((value) => {
          return {
            type: messageTargetFilter.id,
            value,
          };
        });
      })
    : [];
  // eslint-disable-line
  return {
    type: RECIPIENT_TYPES.ORGANIZATION_RECIPIENT,
    organization,
    id: organization.id,
    searchString: `${organization.name}`,
    selectedFilters: [],
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ type: string; value: ComposerOrgs_me_organ... Remove this comment to see the full error message
    filters,
  };
};

const createGroupRecipient = (group: ComposerGroup): GroupRecipient => {
  const { messageTargetFilters } = group;
  const filters = messageTargetFilters
    ? _.flatMap(messageTargetFilters, (messageTargetFilter) => {
        return messageTargetFilter.values.map((value) => {
          return {
            type: messageTargetFilter.id,
            value,
          };
        });
      })
    : [];
  // eslint-disable-line
  return {
    type: RECIPIENT_TYPES.GROUP_RECIPIENT,
    group,
    id: group.id,
    searchString: `${group.className}`,
    selectedFilters: [],
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ type: string; value: ComposerGroups_me_cla... Remove this comment to see the full error message
    filters,
  };
};

export const createGroupChatRecipient = (chat: Chat): GroupChatRecipient => {
  return {
    type: 'group_chat_recipient',
    chat,
    id: chat.id.toString(),
    searchString: `${chat.getTitle()}`,
  };
};

function selectionContainsDistrict(selection: Recipient[]) {
  return selection.some((s) => s.type === 'organization_recipient');
}

function removeRecipientFromSelection(selection: Recipient[], recipient: Recipient) {
  return selection.filter((selectedRecipient) => selectedRecipient.id !== recipient.id);
}

function addRecipientToSelection(selection: Recipient[], recipient: Recipient) {
  const removed = removeRecipientFromSelection(selection, recipient);
  removed.push(recipient);
  return removed;
}

const hasUndefinedCount = (map: RecipientCountsMap): boolean => {
  return map.some((count) => count === undefined);
};

const getTotalCountFromMap = (map: RecipientCountsMap) => {
  if (hasUndefinedCount(map)) {
    return undefined;
  }
  return map.reduce<number>((sum, n) => sum + (n || 0), 0) || 0;
};

// used to remove any map elements that are not also in the list
// returns a new immutable data type
const removeMapItemsNotInList = (map: RecipientCountsMap, list: Recipient[]) => {
  const starter: { [key: string]: boolean } = {};
  const hashList = list.reduce((prev, cur) => {
    prev[cur.id] = true;
    return prev;
  }, starter);

  return map
    .filter((_, kId) => {
      // immutable js seems to have optional arguments for it's iterators?  Weird.
      if (!kId) {
        return false;
      }
      // typescript really gets wonky with the PicK<>
      return hashList[kId as unknown as string];
    })
    .toMap();
};

export enum RBMS_ACTION_TYPES {
  LOAD_ORGANIZATIONS = 'rbms/LOAD_ORGANIZATIONS',
  LOAD_GROUPS = 'rbms/LOAD_GROUPS',
  LOAD_GROUP_CHATS = 'rbms/LOAD_GROUP_CHATS',
  LOAD_GROUP_CHATS_REQUEST = 'rbms/LOAD_GROUP_CHATS_REQUEST',
  LOAD_INDIVIDUALS = 'rbms/LOAD_INDIVIDUALS',
  CHANGE_QUERY = 'rbms/CHANGE_QUERY',
  SAVE_MESSAGE = 'rbms/SAVE_MESSAGE',
  SAVE_ENVELOPE = 'rbms/SAVE_ENVELOPE', // stores some metadata in state for reroute to composer page
  SAVE_DID_SEARCH_RECIPIENTS = 'rbms/SAVE_DID_SEARCH_RECIPIENTS',
  SELECT_STUDENT_ID_LIST_RECIPIENT = 'rbms/SELECT_STUDENT_ID_LIST_RECIPIENT',
  SELECT_RECIPIENT = 'rbms/SELECT_RECIPIENT',
  SELECTION_COUNT_SUCCESS = 'rbms/SELECTION_COUNT_SUCCESS',
  SELECT_INITIAL_TARGETS = 'rbms/SELECT_INITIAL_TARGETS',
  FLUSH = 'rbms/FLUSH',
  CLEAR_SELECTION = 'rbms/CLEAR_SELECTION',
  LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS = 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS',
  LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST = 'rbms/LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST',
  LOAD_GROUP_CHATS_REQUEST_SUCCESS = 'rbms/LOAD_GROUP_CHATS_REQUEST_SUCCESS',
}

type loadOrganizationsAction = {
  type: RBMS_ACTION_TYPES.LOAD_ORGANIZATIONS;
  organizations: ComposerOrganization[];
};

export const loadOrganizations = (
  organizations: ComposerOrganization[]
): loadOrganizationsAction => ({
  type: RBMS_ACTION_TYPES.LOAD_ORGANIZATIONS,
  organizations,
});

type loadGroupsAction = {
  type: RBMS_ACTION_TYPES.LOAD_GROUPS;
  groups: ComposerGroup[];
};

export const loadGroups = (groups: ComposerGroup[]): loadGroupsAction => ({
  type: RBMS_ACTION_TYPES.LOAD_GROUPS,
  groups,
});

type loadGroupChatsAction = {
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS;
  groupChats: Chats;
};

export const loadGroupChats = (groupChats: Chats): loadGroupChatsAction => ({
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS,
  groupChats,
});

type loadIndividualsAction = {
  type: RBMS_ACTION_TYPES.LOAD_INDIVIDUALS;
  members: PotentialChatMember[];
};

export const loadIndividuals = (members: PotentialChatMember[]): loadIndividualsAction => ({
  type: RBMS_ACTION_TYPES.LOAD_INDIVIDUALS,
  members,
});

type changeQueryAction = {
  type: RBMS_ACTION_TYPES.CHANGE_QUERY;
  query: string;
};

export const changeQuery = (query: string): changeQueryAction => ({
  type: RBMS_ACTION_TYPES.CHANGE_QUERY,
  query,
});

type saveMessageAction = {
  type: RBMS_ACTION_TYPES.SAVE_MESSAGE;
  message: string;
};

export const saveMessage = (message: string): saveMessageAction => ({
  type: RBMS_ACTION_TYPES.SAVE_MESSAGE,
  message,
});

type saveEnvelopeAction = {
  type: RBMS_ACTION_TYPES.SAVE_ENVELOPE;
  envelope: Envelope;
};

export const saveEnvelope = (envelope: Envelope): saveEnvelopeAction => ({
  type: RBMS_ACTION_TYPES.SAVE_ENVELOPE,
  envelope,
});

type saveDidSearchRecipientsAction = {
  type: RBMS_ACTION_TYPES.SAVE_DID_SEARCH_RECIPIENTS;
  didSearchRecipients: boolean;
};

export const saveDidSearchRecipients = (
  didSearchRecipients: boolean
): saveDidSearchRecipientsAction => ({
  type: RBMS_ACTION_TYPES.SAVE_DID_SEARCH_RECIPIENTS,
  didSearchRecipients,
});

type selectStudentIdListRecipientAction = {
  type: RBMS_ACTION_TYPES.SELECT_STUDENT_ID_LIST_RECIPIENT;
  recipient: StudentIdListRecipient;
};

export const selectStudentIdListRecipient = (
  recipient: StudentIdListRecipient
): selectStudentIdListRecipientAction => ({
  type: RBMS_ACTION_TYPES.SELECT_STUDENT_ID_LIST_RECIPIENT,
  recipient,
});

type selectRecipientAction = {
  type: RBMS_ACTION_TYPES.SELECT_RECIPIENT;
  recipient: Recipient;
  newFilter?: MessageFilterType;
};

export const internalSelectRecipient = (
  recipient: Recipient,
  newFilter?: MessageFilterType
): selectRecipientAction => ({
  type: RBMS_ACTION_TYPES.SELECT_RECIPIENT,
  recipient,
  newFilter,
});

type selectionCountSuccessAction = {
  type: RBMS_ACTION_TYPES.SELECTION_COUNT_SUCCESS;
  count?: number;
  id: string;
};

export const selectionCountSuccess = (id: string, count?: number): selectionCountSuccessAction => ({
  type: RBMS_ACTION_TYPES.SELECTION_COUNT_SUCCESS,
  count,
  id,
});

const getRecipientType = (recipient: Recipient): string | null => {
  if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
    return 'organization';
  } else if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
    return 'group';
  } else {
    return null;
  }
};

const getRecipientUUID = (recipient: Recipient): string => {
  if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
    return recipient.organization.uuid;
  } else if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
    return recipient.group.uuid;
  } else {
    return '';
  }
};

// Promise<QueryCountRecipients> | Promise<number> | Promise<void>
const getRecipientCountFromRecipient = async (
  recipient: Recipient
): Promise<QueryCountRecipients | null> => {
  const makeQueryCountRecipient = (num: number): QueryCountRecipients => {
    return {
      countRecipients: {
        __typename: 'RecipientsQueryResult',
        count: num,
      },
    };
  };

  if (recipient.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT) {
    return makeQueryCountRecipient(recipient.chat.getMembershipsCount() - 1);
  }

  if (recipient.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT) {
    return makeQueryCountRecipient(1);
  }

  if (recipient.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT) {
    return null;
  }

  try {
    const type = getRecipientType(recipient) ?? '';
    const uuid = getRecipientUUID(recipient);

    const roles = recipient.selectedFilters
      ? recipient.selectedFilters
          .filter((filtered) => filtered.type === 'roles')
          .map((filtered) => filtered.value.id)
      : [];
    const grades = recipient.selectedFilters
      ? recipient.selectedFilters
          .filter((filtered) => filtered.type === 'grades')
          .map((filtered) => filtered.value.id)
      : [];
    const sisLinked = recipient.selectedFilters
      ? recipient.selectedFilters
          .filter((filtered) => filtered.type === 'sisLinked')
          .map((filtered) => filtered.value.id)
      : [];

    let filters = (roles.length ? [{ roles }] : []) as any[];
    if (grades.length) {
      filters.push({ grades });
    }
    if (sisLinked.length) {
      filters.push({ sisLinked });
    }

    const { data } = await getApolloClientSingleton().query<
      QueryCountRecipients,
      QueryCountRecipientsVariables
    >({
      query: RecipientCountQuery,
      variables: { input: { type, uuid, filters } },
    });

    return data;
  } catch (err) {
    // propagate up so we can have a more meaningful error message
    throw err;
  }
};

export const getRecipientCountsFromList = async (
  recipients: Recipient[]
): Promise<Array<QueryCountRecipients | null>> => {
  return Promise.all(recipients.map((recipient) => getRecipientCountFromRecipient(recipient)));
};

export const selectRecipient = (
  recipient: Recipient,
  newFilter?: MessageFilterType,
  preventDispatchInternalSelectRecipient?: boolean
) => {
  return async (dispatch: Function, getState: () => { roleBasedMessagingSearch: State }) => {
    // In some cases we don't want to toggle the selection of the element but trigger other events
    if (!preventDispatchInternalSelectRecipient) {
      await dispatch(internalSelectRecipient(recipient, newFilter));
    }
    const { roleBasedMessagingSearch } = getState();

    const selectedRecipient = roleBasedMessagingSearch.selection.find(
      (rec) => rec.id === recipient.id
    );

    try {
      if (!selectedRecipient) {
        return;
      }

      await dispatch(selectionCountSuccess(`${selectedRecipient.id}`, undefined));
      const count = await getRecipientCountFromRecipient(selectedRecipient);

      if (count) {
        await dispatch(
          selectionCountSuccess(`${selectedRecipient.id}`, count.countRecipients.count)
        );
      }
    } catch (e) {
      console.error('loadSelectedRecipientCount failure', e);
    }
  };
};

export const updateRecipientsCounts = () => {
  return async (dispatch: Function, getState: () => { roleBasedMessagingSearch: State }) => {
    const { roleBasedMessagingSearch } = getState();

    await Promise.all(
      roleBasedMessagingSearch.selection.map(async (selectedRecipient) => {
        try {
          if (!selectedRecipient) {
            return;
          }

          await dispatch(selectionCountSuccess(`${selectedRecipient.id}`, undefined));
          const count = await getRecipientCountFromRecipient(selectedRecipient);

          if (count) {
            await dispatch(
              selectionCountSuccess(`${selectedRecipient.id}`, count.countRecipients.count)
            );
          }
        } catch (e) {
          console.error('loadSelectedRecipientCount failure', e);
        }
      })
    );
  };
};

type selectInitialTargetsAction = {
  type: RBMS_ACTION_TYPES.SELECT_INITIAL_TARGETS;
  targets: Target[];
};

export const selectInitialTargets = (targets: Target[]): selectInitialTargetsAction => ({
  type: RBMS_ACTION_TYPES.SELECT_INITIAL_TARGETS,
  targets,
});

const generateSelectedInitialTargets = (
  selection: Target[],
  targetWithSelectedFilters: Target
): Target[] => {
  if (!selection[0]) {
    return selection;
  }

  return selection.map((s) => {
    return {
      ...s,
      selectedFilters:
        'selectedFilters' in targetWithSelectedFilters
          ? targetWithSelectedFilters.selectedFilters
          : [],
    };
  });
};

type flushAction = {
  type: RBMS_ACTION_TYPES.FLUSH;
};

export const flush = (): flushAction => ({
  type: RBMS_ACTION_TYPES.FLUSH,
});

type clearSelectionAction = {
  type: RBMS_ACTION_TYPES.CLEAR_SELECTION;
};

export const clearSelection = (): clearSelectionAction => ({
  type: RBMS_ACTION_TYPES.CLEAR_SELECTION,
});

export type loadPotentialChatMembersSuccessAction = {
  type: RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS;
  members: Collection<PotentialChatMember>;
};

export const loadPotentialChatMembersSuccess = (
  members: Collection<PotentialChatMember>
): loadPotentialChatMembersSuccessAction => ({
  type: RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS,
  members,
});

export type loadPotentialChatMembersRequestAction = {
  type: RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST;
  query: string;
};

export const loadPotentialChatMembersRequest = (
  query: string
): loadPotentialChatMembersRequestAction => ({
  type: RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST,
  query,
});

export const loadPotentialChatMembers = (query: string) => {
  return async (dispatch: Function) => {
    dispatch(loadPotentialChatMembersRequest(query));

    try {
      const members = await search(query);
      dispatch(loadPotentialChatMembersSuccess(members));
    } catch (e) {
      console.error('loadPotentialChatMembers failure', e);
    }
  };
};

export type loadGroupChatsSuccessAction = {
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST_SUCCESS;
  groupChats: Chats;
};

export const loadGroupChatsSuccess = (groupChats: Chats): loadGroupChatsSuccessAction => ({
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST_SUCCESS,
  groupChats,
});

export type loadGroupChatsRequestAction = {
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST;
  query: string;
};

export const loadGroupChatsRequest = (query: string): loadGroupChatsRequestAction => ({
  type: RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST,
  query,
});

export const searchGroupChats = (query: string) => {
  return async (dispatch: Function) => {
    dispatch(loadGroupChatsRequest(query));

    try {
      const chats = await groupChatsQuery(query);
      dispatch(loadGroupChatsSuccess(chats));
    } catch (e) {
      console.error('loadGroupChats failure', e);
    }
  };
};

export type RbmsAction =
  | loadOrganizationsAction
  | loadGroupsAction
  | loadGroupChatsAction
  | loadIndividualsAction
  | changeQueryAction
  | saveMessageAction
  | saveEnvelopeAction
  | saveDidSearchRecipientsAction
  | selectRecipientAction
  | selectStudentIdListRecipientAction
  | selectInitialTargetsAction
  | flushAction
  | clearSelectionAction
  | loadPotentialChatMembersRequestAction
  | loadPotentialChatMembersSuccessAction
  | loadGroupChatsRequestAction
  | loadGroupChatsSuccessAction
  | selectionCountSuccessAction;

const roleBasedMessagingSearch = (state: State = initialState, action: RbmsAction): State => {
  switch (action.type) {
    case RBMS_ACTION_TYPES.LOAD_ORGANIZATIONS: {
      const { organizations } = action;
      const recipients = organizations.map((o) => createOrgRecipient(o));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], (r) => r.id);
      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_GROUPS: {
      const { groups } = action;
      const recipients = groups
        .filter((g) => g.type !== 'district')
        .map((g) => createGroupRecipient(g));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], (r) => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_GROUP_CHATS: {
      const { groupChats } = action;
      const recipients = groupChats.models?.map((c) => createGroupChatRecipient(c)) ?? [];
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], (r) => r.id);
      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_INDIVIDUALS: {
      const { members } = action;
      const recipients = members.map((m) => createIndividualRecipient(m));
      const { recipients: currentRecipients, ...rest } = state;
      const allRecipients = _.uniqBy([...recipients, ...currentRecipients], (r) => r.id);

      return {
        ...rest,
        results: generateResults(calcResults(allRecipients, state.query)),
        recipients: allRecipients,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST_SUCCESS: {
      // This code is passed the previous set of all concatinated recipients including
      // groups and orgs and expects a return of yet more concatinating results.
      // B/c we do _not_ want results not returned to us by search (if there's a search query)
      // we explicitly filter those out without touching groups or other recipient types.
      const members = action.members ?? []; // `members` are search results.
      const { recipients: currentRecipients, query } = state;

      const allNecessaryRecipients = currentRecipients.filter(
        (r) => r.type !== RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT
      );
      const necessaryFuzzyFilteredRecipients = calcResults(allNecessaryRecipients, query); // Does fuzzy filtering.
      // const orderedMembers = _.orderBy(
      //   members,
      //   member => (member as PotentialChatMember).getSortKey(),
      //   ['asc']
      // ) as PotentialChatMember[];
      const orderedMembers = members; // TODO: order these by sort key. currently could not get test mock to work.
      const searchedRecipients = orderedMembers.map((m) => createIndividualRecipient(m));
      const renderedRecipients = _.uniqBy(
        [...necessaryFuzzyFilteredRecipients, ...searchedRecipients],
        (r) => r.id
      );

      return {
        ...state,
        results: generateResults(renderedRecipients),
        recipients: _.uniqBy([...currentRecipients, ...searchedRecipients], (r) => r.id),
        loadingIndividualRecipients: false,
        query,
      };
    }

    case RBMS_ACTION_TYPES.CHANGE_QUERY: {
      const { query } = action;
      return {
        ...state,
        results: generateResults(calcResults(state.recipients, query)),
        resultSelectionIndex: 0,
        query,
      };
    }

    case RBMS_ACTION_TYPES.SAVE_MESSAGE: {
      const { message } = action;
      return {
        ...state,
        message,
      };
    }

    case RBMS_ACTION_TYPES.SAVE_ENVELOPE: {
      const { envelope } = action;
      return { ...state, envelope };
    }

    case RBMS_ACTION_TYPES.SAVE_DID_SEARCH_RECIPIENTS: {
      const { didSearchRecipients } = action;
      return { ...state, didSearchRecipients };
    }

    case RBMS_ACTION_TYPES.SELECT_STUDENT_ID_LIST_RECIPIENT: {
      const { recipient } = action;
      const { selection } = state;
      const recipientInSelection = selection.find(
        (selectedRecipient) => selectedRecipient.id === recipient.id
      );

      const selectionWithRecipientAdded = addRecipientToSelection(selection, recipient);
      const selectionWithRecipientRemoved = removeRecipientFromSelection(selection, recipient);
      const recipientSelection = recipientInSelection
        ? [...selectionWithRecipientRemoved, recipient]
        : selectionWithRecipientAdded;
      return {
        ...state,
        selection: recipientSelection,
        targets: createTargets(recipientSelection),
      };
    }

    case RBMS_ACTION_TYPES.SELECT_RECIPIENT: {
      const { recipient, newFilter } = action;
      const { selection, recipientCountsMap } = state;
      const recipientInSelection = selection.find(
        (selectedRecipient) => selectedRecipient.id === recipient.id
      );

      if (
        recipient.type !== RECIPIENT_TYPES.ORGANIZATION_RECIPIENT &&
        recipient.type !== RECIPIENT_TYPES.GROUP_RECIPIENT
      ) {
        if (recipientInSelection) {
          const selectionWithRecipientRemoved = removeRecipientFromSelection(selection, recipient);
          const newMap = removeMapItemsNotInList(recipientCountsMap, selectionWithRecipientRemoved);
          return {
            ...state,
            districtIsSelected: selectionContainsDistrict(selectionWithRecipientRemoved),
            selection: selectionWithRecipientRemoved,
            recipientCountsMap: newMap,
            totalRecipientCount: getTotalCountFromMap(newMap),
            targets: createTargets(selectionWithRecipientRemoved),
          };
        } else {
          const selectionWithRecipientAdded = addRecipientToSelection(selection, recipient);
          const newMap = removeMapItemsNotInList(recipientCountsMap, selectionWithRecipientAdded);
          return {
            ...state,
            districtIsSelected: selectionContainsDistrict(selectionWithRecipientAdded),
            selection: selectionWithRecipientAdded,
            recipientCountsMap: newMap,
            totalRecipientCount: getTotalCountFromMap(newMap),
            targets: createTargets(selectionWithRecipientAdded),
          };
        }
      }

      let recipientSelection = [...selection];
      const existingFiltersOnRecipient =
        recipientInSelection &&
        (recipientInSelection.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT ||
          recipientInSelection.type === RECIPIENT_TYPES.GROUP_RECIPIENT)
          ? recipientInSelection.selectedFilters
          : [];
      const unfilteredRecipient = { ...recipient, selectedFilters: [] };
      if (newFilter) {
        const recipientWithNewFilterAdded = {
          ...recipient,
          selectedFilters: [...existingFiltersOnRecipient, newFilter],
        };
        const recipientWithNewFilterRemoved = {
          ...recipient,
          selectedFilters: existingFiltersOnRecipient.filter(
            (existingFilter) => existingFilter.value.id !== newFilter.value.id
          ),
        };
        const recipientWithNewFilterTypeRemoved = {
          ...recipient,
          selectedFilters: existingFiltersOnRecipient.filter(
            (existingFilter) => existingFilter.type !== newFilter.type
          ),
        };
        const newFilterAlreadyExistsOnRecipient = existingFiltersOnRecipient.some(
          (existingFilter) => existingFilter.value.id === newFilter.value.id
        );
        const newFilterIsTheOnlyFilterOnRecipient =
          newFilterAlreadyExistsOnRecipient && existingFiltersOnRecipient.length === 1;
        const newFilterSelectsAllOfOneType = newFilter.value.id === 'everyone';
        const existingFiltersAreSameTypeAsNewFilter = existingFiltersOnRecipient
          ? existingFiltersOnRecipient.some(
              (existingFilter) => existingFilter.type === newFilter.type
            )
          : false;

        if (newFilterSelectsAllOfOneType && recipientInSelection) {
          recipientSelection = existingFiltersAreSameTypeAsNewFilter
            ? addRecipientToSelection(selection, recipientWithNewFilterTypeRemoved)
            : [...selection];
        } else if (newFilterSelectsAllOfOneType && !recipientInSelection) {
          recipientSelection = addRecipientToSelection(selection, unfilteredRecipient);
        } else if (newFilterIsTheOnlyFilterOnRecipient) {
          recipientSelection = removeRecipientFromSelection(selection, recipient);
        } else {
          recipientSelection = newFilterAlreadyExistsOnRecipient
            ? addRecipientToSelection(selection, recipientWithNewFilterRemoved)
            : addRecipientToSelection(selection, recipientWithNewFilterAdded);
        }
      } else {
        recipientSelection = recipientInSelection
          ? removeRecipientFromSelection(selection, recipient)
          : addRecipientToSelection(selection, unfilteredRecipient);
      }

      const newMap = removeMapItemsNotInList(recipientCountsMap, recipientSelection);

      return {
        ...state,
        districtIsSelected: selectionContainsDistrict(recipientSelection),
        selection: recipientSelection,
        recipientCountsMap: newMap,
        totalRecipientCount: getTotalCountFromMap(newMap),
        targets: createTargets(recipientSelection),
      };
    }

    case RBMS_ACTION_TYPES.SELECT_INITIAL_TARGETS: {
      const { targets } = action;
      const selection = targets
        .map((t) => {
          return state.recipients.find((r) => {
            if ('group_id' in t) {
              if (r.id === t.group_id) {
                return true;
              }
            }

            if ('org_id' in t) {
              return r.id === t.org_id;
            }

            return false;
          });
        })
        .filter(Boolean);

      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(Recipient | undefined)[] | ({ s... Remove this comment to see the full error message
      const selectionWithTargetFilters = generateSelectedInitialTargets(selection, targets[0]);

      return {
        ...state,
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(Recipient | undefined)[] | ({ s... Remove this comment to see the full error message
        selection: selectionWithTargetFilters,
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(Recipient | undefined)[] | ({ s... Remove this comment to see the full error message
        districtIsSelected: selectionContainsDistrict(selectionWithTargetFilters),
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(Recipient | undefined)[] | ({ s... Remove this comment to see the full error message
        targets: createTargets(selectionWithTargetFilters),
      };
    }

    case RBMS_ACTION_TYPES.FLUSH: {
      return initialState;
    }

    case RBMS_ACTION_TYPES.CLEAR_SELECTION: {
      return {
        ...state,
        selection: [],
        targets: [],
        districtIsSelected: false,
        totalRecipientCount: undefined,
        recipientCountsMap: state.recipientCountsMap.clear(),
      };
    }

    case RBMS_ACTION_TYPES.LOAD_POTENTIAL_CHAT_MEMBERS_REQUEST: {
      return {
        ...state,
        query: action.query,
        loadingIndividualRecipients: true,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST: {
      return {
        ...state,
        query: action.query,
        loadingGroupChats: true,
      };
    }

    case RBMS_ACTION_TYPES.LOAD_GROUP_CHATS_REQUEST_SUCCESS: {
      const { groupChats } = action;
      const { recipients: currentRecipients, query } = state;
      // We may have filtered during other passes and want to maintain those filters.
      let currentResults = state.results.flatMap((x) => x) ?? currentRecipients;
      let allRecipients = [...currentRecipients];
      if (groupChats) {
        const recipients = groupChats.models?.map((gc) => createGroupChatRecipient(gc)) ?? [];
        allRecipients = [...allRecipients, ...recipients];
        currentResults = [...currentResults, ...recipients];
      }
      allRecipients = _.uniqBy(allRecipients, (r) => r.id);
      currentResults = _.uniqBy(currentResults, (r) => r.id);

      return {
        ...state,
        results: generateResults(calcResults(currentResults, query)),
        recipients: allRecipients,
        loadingGroupChats: false,
        query,
      };
    }

    case RBMS_ACTION_TYPES.SELECTION_COUNT_SUCCESS: {
      const { count, id } = action;
      const { recipientCountsMap } = state;
      const newMap = recipientCountsMap.set(id, count);
      return {
        ...state,
        recipientCountsMap: newMap,
        totalRecipientCount: getTotalCountFromMap(newMap),
      };
    }

    default:
      return state;
  }
};

export default roleBasedMessagingSearch;
